import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiBox,
  FiChevronRight,
  FiEye,
  FiPackage,
  FiRefreshCw,
  FiSearch,
  FiTruck,
  FiX,
} from "react-icons/fi";

import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return "—";

  return new Intl.DateTimeFormat("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
};

const titleCase = (value) =>
  String(value || "")
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const getErrorMessage = (error) => {
  if (Array.isArray(error?.details?.message)) {
    return error.details.message.join(", ");
  }

  return error?.message || "Something went wrong. Please try again.";
};

const statusClasses = {
  CONFIRMED: "bg-blue-50 text-blue-700",
  PROCESSING: "bg-amber-50 text-amber-700",
  PARTIALLY_SHIPPED: "bg-purple-50 text-purple-700",
  SHIPPED: "bg-green-50 text-green-700",
  DELIVERED: "bg-emerald-50 text-emerald-700",
  CANCELLED: "bg-red-50 text-red-700",
  PENDING_PAYMENT: "bg-orange-50 text-orange-700",
};

const allocationClasses = {
  RESERVED: "bg-blue-50 text-blue-700",
  PACKING: "bg-amber-50 text-amber-700",
  SHIPPED: "bg-green-50 text-green-700",
  DELIVERED: "bg-emerald-50 text-emerald-700",
  RELEASED: "bg-gray-100 text-gray-700",
  CANCELLED: "bg-red-50 text-red-700",
};

const StatusBadge = ({ value, allocation = false }) => (
  <span
    className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${
      (allocation ? allocationClasses[value] : statusClasses[value]) ||
      "bg-gray-100 text-gray-700"
    }`}
  >
    {titleCase(value)}
  </span>
);

export default function EcommerceOrdersPage() {
  const { accessToken, user } = useAuth();

  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [shippingAllocationId, setShippingAllocationId] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const canShip =
    !Array.isArray(user?.permissions) ||
    user.permissions.includes("ecommerce_orders.ship");

  const loadOrders = useCallback(async () => {
    if (!accessToken) return;

    setLoadingOrders(true);
    setError("");

    try {
      const response = await apiRequest("/admin/ecommerce-orders", {
        accessToken,
      });

      setOrders(response || []);
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setLoadingOrders(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const openOrder = async (orderId) => {
    setLoadingDetail(true);
    setError("");
    setNotice("");

    try {
      const order = await apiRequest(`/admin/ecommerce-orders/${orderId}`, {
        accessToken,
      });

      setSelectedOrder(order);
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setLoadingDetail(false);
    }
  };

  const shipAllocation = async (allocationId) => {
    if (
      !window.confirm(
        "Mark this allocated item as shipped? This will reduce on-hand stock and create a SALE_ISSUE inventory movement.",
      )
    ) {
      return;
    }

    setShippingAllocationId(allocationId);
    setError("");
    setNotice("");

    try {
      await apiRequest(
        `/admin/ecommerce-orders/allocations/${allocationId}/ship`,
        {
          method: "POST",
          accessToken,
        },
      );

      setNotice("Allocation shipped and inventory movement recorded.");

      if (selectedOrder?.id) {
        await openOrder(selectedOrder.id);
      }

      await loadOrders();
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setShippingAllocationId(null);
    }
  };

  const filteredOrders = useMemo(() => {
    const query = search.trim().toLowerCase();

    return orders.filter((order) => {
      const matchesStatus =
        statusFilter === "ALL" || order.status === statusFilter;

      const matchesSearch =
        !query ||
        order.orderNumber?.toLowerCase().includes(query) ||
        order.customer?.fullName?.toLowerCase().includes(query) ||
        order.customer?.email?.toLowerCase().includes(query) ||
        order.customer?.phone?.toLowerCase().includes(query);

      return matchesStatus && matchesSearch;
    });
  }, [orders, search, statusFilter]);

  const summary = useMemo(
    () => ({
      total: orders.length,
      confirmed: orders.filter((order) => order.status === "CONFIRMED").length,
      partial: orders.filter(
        (order) => order.status === "PARTIALLY_SHIPPED",
      ).length,
      shipped: orders.filter((order) => order.status === "SHIPPED").length,
    }),
    [orders],
  );

  return (
    <div>
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5 mb-8">
        <div>
          <p className="text-sm text-gray-500">Sales / Ecommerce</p>

          <h1 className="mt-1 text-3xl font-bold text-gray-900">
            Ecommerce Orders
          </h1>

          <p className="mt-2 text-gray-500">
            View branch-allocated online orders and ship reserved stock.
          </p>
        </div>

        <button
          onClick={loadOrders}
          disabled={loadingOrders}
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
        >
          <FiRefreshCw className={loadingOrders ? "animate-spin" : ""} />
          Refresh Orders
        </button>
      </div>

      {error && (
        <div className="mb-6 rounded-2xl border border-red-100 bg-red-50 px-5 py-4 text-sm font-medium text-red-700">
          {error}
        </div>
      )}

      {notice && (
        <div className="mb-6 rounded-2xl border border-green-100 bg-green-50 px-5 py-4 text-sm font-medium text-green-700">
          {notice}
        </div>
      )}

      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-5 mb-7">
        {[
          {
            label: "Allocated Orders",
            value: summary.total,
            color: "bg-primary-50 text-primary-800",
            icon: FiBox,
          },
          {
            label: "Ready to Ship",
            value: summary.confirmed,
            color: "bg-blue-50 text-blue-700",
            icon: FiPackage,
          },
          {
            label: "Partially Shipped",
            value: summary.partial,
            color: "bg-purple-50 text-purple-700",
            icon: FiTruck,
          },
          {
            label: "Fully Shipped",
            value: summary.shipped,
            color: "bg-green-50 text-green-700",
            icon: FiTruck,
          },
        ].map((item) => {
          const Icon = item.icon;

          return (
            <div
              key={item.label}
              className="rounded-3xl border border-gray-100 bg-white p-5"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-500">{item.label}</p>
                  <p className="mt-2 text-3xl font-bold text-gray-900">
                    {item.value}
                  </p>
                </div>

                <div
                  className={`h-11 w-11 rounded-2xl flex items-center justify-center ${item.color}`}
                >
                  <Icon size={21} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="rounded-3xl border border-gray-100 bg-white">
        <div className="flex flex-col lg:flex-row gap-4 p-5 sm:p-6 border-b border-gray-100">
          <div className="flex flex-1 items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4">
            <FiSearch className="text-gray-400" />

            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search order number, customer, email or phone..."
              className="h-12 min-w-0 flex-1 bg-transparent text-sm outline-none"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 bg-white px-4 text-sm font-medium outline-none"
          >
            <option value="ALL">All Statuses</option>
            <option value="CONFIRMED">Confirmed</option>
            <option value="PARTIALLY_SHIPPED">Partially Shipped</option>
            <option value="SHIPPED">Shipped</option>
            <option value="DELIVERED">Delivered</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>

        {loadingOrders ? (
          <div className="p-6 space-y-4">
            {[1, 2, 3, 4].map((item) => (
              <div
                key={item}
                className="h-20 rounded-2xl bg-gray-100 animate-pulse"
              />
            ))}
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="px-6 py-20 text-center">
            <div className="mx-auto h-14 w-14 rounded-2xl bg-primary-50 text-primary-800 flex items-center justify-center">
              <FiBox size={25} />
            </div>

            <h2 className="mt-5 text-xl font-bold text-gray-900">
              No ecommerce orders found
            </h2>

            <p className="mt-2 text-sm text-gray-500">
              Orders allocated to your accessible stores will appear here.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50 text-left">
                <tr className="text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-6 py-4 font-semibold">Order</th>
                  <th className="px-6 py-4 font-semibold">Customer</th>
                  <th className="px-6 py-4 font-semibold">Date</th>
                  <th className="px-6 py-4 font-semibold">Total</th>
                  <th className="px-6 py-4 font-semibold">Status</th>
                  <th className="px-6 py-4 font-semibold text-right">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {filteredOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-5">
                      <p className="font-bold text-gray-900">
                        {order.orderNumber}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        {order._count?.items || order.items?.length || 0} item
                        {(order._count?.items || order.items?.length || 0) !==
                        1
                          ? "s"
                          : ""}
                      </p>
                    </td>

                    <td className="px-6 py-5">
                      <p className="font-semibold text-gray-900">
                        {order.customer?.fullName || "Customer"}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        {order.customer?.phone || order.customer?.email || "—"}
                      </p>
                    </td>

                    <td className="px-6 py-5 text-sm text-gray-600">
                      {formatDate(order.createdAt)}
                    </td>

                    <td className="px-6 py-5 font-bold text-gray-900">
                      {formatCurrency(order.totalAmount)}
                    </td>

                    <td className="px-6 py-5">
                      <StatusBadge value={order.status} />
                    </td>

                    <td className="px-6 py-5 text-right">
                      <button
                        onClick={() => openOrder(order.id)}
                        className="inline-flex items-center gap-2 rounded-xl bg-primary-50 px-3 py-2 text-sm font-bold text-primary-800 hover:bg-primary-100"
                      >
                        <FiEye />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(selectedOrder || loadingDetail) && (
        <div className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-sm p-4 sm:p-6 flex justify-end">
          <div className="h-full w-full max-w-3xl overflow-y-auto rounded-3xl bg-[#F8F6F5] shadow-2xl">
            {loadingDetail || !selectedOrder ? (
              <div className="p-8 space-y-5">
                <div className="h-10 w-56 rounded-xl bg-gray-200 animate-pulse" />
                <div className="h-40 rounded-3xl bg-white animate-pulse" />
                <div className="h-64 rounded-3xl bg-white animate-pulse" />
              </div>
            ) : (
              <>
                <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-gray-100 bg-white p-6">
                  <div>
                    <p className="text-sm text-gray-500">Ecommerce Order</p>
                    <h2 className="mt-1 text-2xl font-bold text-gray-900">
                      {selectedOrder.orderNumber}
                    </h2>

                    <div className="mt-3">
                      <StatusBadge value={selectedOrder.status} />
                    </div>
                  </div>

                  <button
                    onClick={() => setSelectedOrder(null)}
                    className="h-10 w-10 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
                    aria-label="Close order details"
                  >
                    <FiX size={20} />
                  </button>
                </div>

                <div className="p-5 sm:p-7 space-y-6">
                  <section className="rounded-3xl bg-white border border-gray-100 p-6">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-gray-400">
                          Customer
                        </p>
                        <p className="mt-2 font-bold text-gray-900">
                          {selectedOrder.customer?.fullName}
                        </p>
                        <p className="mt-1 text-sm text-gray-500">
                          {selectedOrder.customer?.email}
                        </p>
                        <p className="mt-1 text-sm text-gray-500">
                          {selectedOrder.customer?.phone}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-gray-400">
                          Delivery Address
                        </p>
                        <p className="mt-2 text-sm leading-6 text-gray-700">
                          {selectedOrder.recipientName}
                          <br />
                          {selectedOrder.shippingAddressLine1}
                          {selectedOrder.shippingAddressLine2
                            ? `, ${selectedOrder.shippingAddressLine2}`
                            : ""}
                          <br />
                          {selectedOrder.shippingCity},{" "}
                          {selectedOrder.shippingState}{" "}
                          {selectedOrder.shippingPostalCode}
                          <br />
                          {selectedOrder.recipientPhone}
                        </p>
                      </div>
                    </div>
                  </section>

                  <section className="rounded-3xl bg-white border border-gray-100 p-6">
                    <div className="flex justify-between gap-4">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">
                          Items and Fulfilment
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">
                          Ship each reserved allocation from its assigned
                          store/warehouse.
                        </p>
                      </div>

                      <p className="text-lg font-bold text-gray-900">
                        {formatCurrency(selectedOrder.totalAmount)}
                      </p>
                    </div>

                    <div className="mt-6 space-y-5">
                      {selectedOrder.items?.map((item) => (
                        <div
                          key={item.id}
                          className="rounded-2xl border border-gray-100 p-5"
                        >
                          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                            <div>
                              <p className="font-bold text-gray-900">
                                {item.productNameSnapshot}
                              </p>

                              <p className="mt-1 text-sm text-gray-500">
                                SKU: {item.skuSnapshot}
                                {item.variantNameSnapshot
                                  ? ` · ${item.variantNameSnapshot}`
                                  : ""}
                              </p>

                              <p className="mt-2 text-sm font-semibold text-gray-700">
                                Qty: {item.quantity} ·{" "}
                                {formatCurrency(item.unitPrice)} each
                              </p>
                            </div>

                            <p className="font-bold text-gray-900">
                              {formatCurrency(item.lineTotalAmount)}
                            </p>
                          </div>

                          <div className="mt-5 space-y-3">
                            {item.allocations?.map((allocation) => (
                              <div
                                key={allocation.id}
                                className="rounded-2xl bg-gray-50 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                              >
                                <div>
                                  <p className="font-semibold text-gray-900">
                                    {allocation.store?.name}
                                  </p>

                                  <p className="mt-1 text-xs text-gray-500">
                                    {allocation.store?.code} ·{" "}
                                    {allocation.store?.type} · Qty{" "}
                                    {allocation.quantity}
                                  </p>

                                  <div className="mt-2">
                                    <StatusBadge
                                      value={allocation.status}
                                      allocation
                                    />
                                  </div>
                                </div>

                                {allocation.status === "RESERVED" &&
                                  canShip && (
                                    <button
                                      onClick={() =>
                                        shipAllocation(allocation.id)
                                      }
                                      disabled={
                                        shippingAllocationId === allocation.id
                                      }
                                      className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary-800 px-4 py-3 text-sm font-bold text-white hover:bg-primary-900 disabled:opacity-60"
                                    >
                                      <FiTruck />
                                      {shippingAllocationId === allocation.id
                                        ? "Shipping..."
                                        : "Mark Shipped"}
                                    </button>
                                  )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>

                  <section className="rounded-3xl bg-white border border-gray-100 p-6">
                    <h3 className="text-lg font-bold text-gray-900">
                      Payment Summary
                    </h3>

                    <div className="mt-5 space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Payment Method</span>
                        <span className="font-semibold text-gray-900">
                          {titleCase(selectedOrder.paymentMethod)}
                        </span>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-gray-500">Payment Status</span>
                        <span className="font-semibold text-gray-900">
                          {titleCase(selectedOrder.paymentStatus)}
                        </span>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-gray-500">Subtotal</span>
                        <span className="font-semibold text-gray-900">
                          {formatCurrency(selectedOrder.subtotalAmount)}
                        </span>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-gray-500">Shipping</span>
                        <span className="font-semibold text-gray-900">
                          {formatCurrency(selectedOrder.shippingAmount)}
                        </span>
                      </div>

                      <div className="border-t border-gray-100 pt-3 flex justify-between text-base">
                        <span className="font-bold text-gray-900">
                          Total Amount
                        </span>
                        <span className="font-bold text-gray-900">
                          {formatCurrency(selectedOrder.totalAmount)}
                        </span>
                      </div>
                    </div>
                  </section>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}